import {
    createFolderFailedDescription,
    renameFolderFailedDescription,
    removePublicFolderFailedDescription,
    deleteFolderFailedDescription,
} from './folderManagementDataActions.locstring.json';
import { cancelLabel } from 'owa-locstrings/lib/strings/cancellabel.locstring.json';
import { createFolderAlreadyExistsDescription } from '../../strings.locstring.json';
import { somethingWentWrongLabel } from 'owa-locstrings/lib/strings/somethingwentwronglabel.locstring.json';
import loc, { format } from 'owa-localize';
import {
    createDataActionFactory,
    createActionFactory,
    ContactFolder,
    addNotification,
    Feature,
    createThunk,
    hashFolderId,
    rootContactFolderDistinguishedName,
    ThunkDispatch,
    getMatchingFavorite,
} from '../../../shared';
import {
    createFolder as owsCreateFolder,
    isCreateFolderExpectedError,
    CreateFolderExpectedError,
} from '../../dataLayer/createFolder';
import { deleteFolder as owsDeleteFolder } from '../../dataLayer/deleteFolder';
import { renameFolder as owsRenameFolder } from '../../dataLayer/renameFolder';
import { hardDeleteFolder as owsHardDeleteFolder } from '../../dataLayer/hardDeleteFolder';
import { moveFolder as owsMoveFolder } from '../../dataLayer/moveFolder';
import { removeFavoritePublicFolder as owsRemoveFavoritePublicFolder } from '../../dataLayer/removeFavoritePublicFolder';
import { subscribeToFolderNotifications, unfavoriteMultiplePersonas, Person } from '../../../hub';
import type { Dispatch } from 'redux';

import { getGuid } from 'owa-guid';
import {
    getAppState,
    AppState,
    invalidateCachedContacts,
    getFavorites,
    ContactFoldersState,
} from '../../../appstate';

// Set to 8 character to the first non-random value in a random GUID.
const FOLDER_RENAME_RANDOM_LENGTH = 8;

export const createFolderCompletedAction = createDataActionFactory<
    { newFolder: ContactFolder },
    Error & { folderName: string }
>('CreateFolderCompletedAction', Feature.FolderManagement);

export const createFolderAlreadyExistsAction = createActionFactory<{ folderName: string }>(
    'CreateFolderAlreadyExistsAction',
    Feature.FolderManagement
);

export const renameFolderCompletedAction = createDataActionFactory<
    { folderId: string; newName: string },
    Error & { folderId: string }
>('RenameFolderCompletedAction', Feature.FolderManagement);

export const deleteFolderCompletedAction = createDataActionFactory<
    { folderId: string },
    Error & { folderId: string; message: string }
>(
    'DeleteFolderCompletedAction',
    Feature.FolderManagement,
    action => ({
        folderId: hashFolderId(action.payload.folderId),
    }),
    action => ({
        folderId: hashFolderId(action.payload.folderId),
        message: action.payload.message,
    })
);

export const hardDeleteFolderCompletedAction = createDataActionFactory<
    { folderId: string },
    Error & { folderId: string; message: string }
>(
    'HardDeleteFolderCompletedAction',
    Feature.FolderManagement,
    action => ({
        folderId: hashFolderId(action.payload.folderId),
    }),
    action => ({
        folderId: hashFolderId(action.payload.folderId),
        message: action.payload.message,
    })
);

export const moveFolderCompletedAction = createDataActionFactory<
    { folderId: string; toFolderId: string },
    Error & { folderId: string; toFolderId: string; message: string }
>(
    'MoveFolderCompletedAction',
    Feature.FolderManagement,
    action => ({
        folderId: hashFolderId(action.payload.folderId),
        toFolderId: hashFolderId(action.payload.folderId),
    }),
    action => ({
        folderId: hashFolderId(action.payload.folderId),
        toFolderId: hashFolderId(action.payload.toFolderId),
        message: action.payload.message,
    })
);

export const removeFavoritePublicFolderCompletedAction = createDataActionFactory<
    { folderId: string },
    { folderId: string }
>(
    'RemoveFavoritePublicFolderCompletedAction',
    Feature.FolderManagement,
    action => ({
        folderId: hashFolderId(action.payload.folderId),
    }),
    action => ({
        folderId: hashFolderId(action.payload.folderId),
    })
);

export const createFolder = (dispatch: Dispatch) => (
    name: string,
    rootFolderId: string,
    skipErrorIfExists?: boolean
): Promise<ContactFolder | CreateFolderExpectedError | void> => {
    return owsCreateFolder(rootFolderId, name).then(
        newFolder => {
            if (isCreateFolderExpectedError(newFolder)) {
                dispatch(createFolderAlreadyExistsAction({ folderName: name }));
                if (!skipErrorIfExists) {
                    addNotification(
                        loc(somethingWentWrongLabel),
                        format(loc(createFolderAlreadyExistsDescription), name)
                    );
                }
            } else {
                dispatch(createFolderCompletedAction.success({ newFolder }));
                subscribeToFolderNotifications(newFolder.id, dispatch);
            }
            return newFolder;
        },
        (error: Error) => {
            dispatch(createFolderCompletedAction.error({ ...error, folderName: name }));
            addNotification(
                loc(somethingWentWrongLabel),
                format(loc(createFolderFailedDescription), name)
            );
        }
    );
};

export const renameFolder = (dispatch: Dispatch) => (
    folderId: string,
    newName: string,
    oldName: string
): Promise<void> => {
    return owsRenameFolder(folderId, newName).then(
        response => {
            dispatch(renameFolderCompletedAction.success({ folderId, newName }));
        },
        (error: Error) => {
            dispatch(renameFolderCompletedAction.error({ ...error, folderId }));
            addNotification(
                loc(somethingWentWrongLabel),
                format(loc(renameFolderFailedDescription), oldName),
                loc(cancelLabel)
            );
        }
    );
};

export const deleteFolder = (
    folderId: string,
    folderName: string,
    folderRenamed: boolean = false
) =>
    createThunk('DeleteFolder', (dispatch: ThunkDispatch, getState: () => {}) => {
        owsDeleteFolder(folderId)
            .then(success => {
                if (success) {
                    const appState = getAppState(getState());
                    postDeleteOperations(folderId, dispatch, appState);

                    dispatch(deleteFolderCompletedAction.success({ folderId }));
                } else {
                    if (folderRenamed) {
                        // Deleting renamed folder failed. Rename folder back to original name.
                        const originalFolderName = getOriginalFolderName(folderName);
                        owsRenameFolder(folderId, originalFolderName);
                    } else {
                        // In case a folder with same name has been deleted before, post fix the folder name with random
                        // characters. For more background info:
                        // https://msfast.visualstudio.com/FAST/Office%20People%20HD/_workitems/edit/230494
                        const deleteFolderName =
                            folderName + '_' + getGuid().substr(0, FOLDER_RENAME_RANDOM_LENGTH);
                        owsRenameFolder(folderId, deleteFolderName).then(
                            response => {
                                dispatch(deleteFolder(folderId, deleteFolderName, true));
                            },
                            (error: Error) => {
                                handleDeleteFolderError(
                                    dispatch,
                                    error,
                                    folderId,
                                    folderName,
                                    getState
                                );
                            }
                        );

                        return;
                    }

                    throw new Error('DeleteContactFolder_failed');
                }
            })
            .catch((error: Error) => {
                if (folderRenamed) {
                    folderName = getOriginalFolderName(folderName);
                }

                handleDeleteFolderError(dispatch, error, folderId, folderName, getState);
            });
    });

export const hardDeleteFolder = (folderId: string) =>
    createThunk('HardDeleteFolder', (dispatch: ThunkDispatch, getState: () => {}) => {
        owsHardDeleteFolder(folderId)
            .then(response => dispatch(hardDeleteFolderCompletedAction.success({ folderId })))
            .catch((error: Error) => {
                dispatch(hardDeleteFolderCompletedAction.error({ ...error, folderId }));
            });
    });

export const moveFolder = (folderId: string, toFolderId: string) =>
    createThunk('MoveFolder', (dispatch: ThunkDispatch, getState: () => {}) => {
        owsMoveFolder(folderId, toFolderId)
            .then(response => dispatch(moveFolderCompletedAction.success({ folderId, toFolderId })))
            .catch((error: Error) => {
                dispatch(moveFolderCompletedAction.error({ ...error, folderId, toFolderId }));
            });
    });

export const removeFavoritePublicFolder = (dispatch: Dispatch) => (
    folderId: string,
    folderName: string
): Promise<void> => {
    return owsRemoveFavoritePublicFolder(folderId).then(
        success => {
            if (success) {
                dispatch(removeFavoritePublicFolderCompletedAction.success({ folderId }));
            } else {
                dispatch(removeFavoritePublicFolderCompletedAction.error({ folderId }));
                addNotification(
                    loc(somethingWentWrongLabel),
                    format(loc(removePublicFolderFailedDescription), folderName)
                );
            }
        },
        (error: Error) => {
            dispatch(removeFavoritePublicFolderCompletedAction.error({ folderId }));
            addNotification(
                loc(somethingWentWrongLabel),
                format(loc(removePublicFolderFailedDescription), folderName)
            );
        }
    );
};

function getOriginalFolderName(folderName: string): string {
    return folderName.slice(0, folderName.length - (FOLDER_RENAME_RANDOM_LENGTH + 1));
}

function handleDeleteFolderError(
    dispatch: Dispatch,
    error: Error,
    folderId: string,
    folderName: string,
    getState: () => {}
): void {
    const appState = getAppState(getState());
    const folder = getFolderById(folderId, appState.data.contactFoldersState);
    const defaultFolderId = appState.data.contactFoldersState.defaultContactFolder?.id;

    let message = ' - Deleting folder from group: ' + folder.group;

    if (defaultFolderId === folderId) {
        message += ' - Folder marked for deletion is default folder';
    }

    if (folderName === 'contacts' || folderName === rootContactFolderDistinguishedName) {
        message += ' - Folder named: ' + folderName;
    }

    dispatch(deleteFolderCompletedAction.error({ ...error, folderId: folderId, message: message }));
    addNotification(
        loc(somethingWentWrongLabel),
        format(loc(deleteFolderFailedDescription), folderName),
        loc(cancelLabel)
    );
}

function postDeleteOperations(folderId: string, dispatch: ThunkDispatch, appState: AppState): void {
    const favorites = getFavorites(appState);
    const hubPersonaIdsForFolder =
        appState.data.contactFoldersState.folderIdToHubPersonaIds[folderId] || [];
    const hubPersonasMap = appState.data.personas;
    const deletedPersonaIds = hubPersonaIdsForFolder.map(
        hubPersonaId => (hubPersonasMap[hubPersonaId] as Person).personaId
    );
    const deletedFavorites = hubPersonaIdsForFolder
        .map(hubPersonaId => getMatchingFavorite(hubPersonasMap[hubPersonaId], favorites))
        .filter(favorite => !!favorite);

    invalidateCachedContacts(dispatch)(deletedPersonaIds);
    unfavoriteMultiplePersonas(deletedFavorites, dispatch);
}

function getFolderById(folderId: string, contactFolderState: ContactFoldersState): ContactFolder {
    return [
        ...(contactFolderState.displayedContactFolders || []),
        ...(contactFolderState.defaultContactFolder
            ? [contactFolderState.defaultContactFolder]
            : []),
        ...(contactFolderState.rootContactFolder ? [contactFolderState.rootContactFolder] : []),
    ].find(folder => folder.id === folderId);
}
export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
